import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Eye, EyeOff, ArrowRight, ShieldCheck } from "lucide-react";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (pw.length < 6) return toast.error("Password must be 6+ characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    if (!token) return toast.error("Missing reset token");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated. Please log in.");
      setTimeout(() => nav("/login"), 1200);
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-10 sm:px-6" data-testid="reset-password-page">
      <div className="sarthi-card p-6">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sarthi-secondary text-white">
            <ShieldCheck className="h-6 w-6" strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="font-display text-2xl font-extrabold text-sarthi-text">Reset password</h1>
            <p className="text-sm font-medium text-sarthi-muted">Choose a new password for your Sarthi account.</p>
          </div>
        </div>

        {done ? (
          <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-4 text-sm font-bold text-emerald-800">
            Password updated. Redirecting to login…
          </div>
        ) : (
          <div className="space-y-4">
            <PwField testid="new-password" label="New password" show={show} value={pw} onChange={setPw} />
            <PwField testid="confirm-password" label="Confirm password" show={show} value={pw2} onChange={setPw2} />
            <label className="flex items-center gap-2 text-xs font-semibold text-sarthi-muted">
              <input type="checkbox" checked={show} onChange={() => setShow((v) => !v)} data-testid="show-passwords" />
              Show passwords
            </label>
            <button
              data-testid="reset-submit"
              onClick={submit}
              disabled={busy || !pw || !pw2}
              className="sarthi-btn-primary flex w-full items-center justify-center gap-2 py-4 text-lg text-white disabled:opacity-50"
            >
              {busy ? "Saving…" : "Update password"} <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
      <div className="mt-4 text-center text-sm font-semibold text-sarthi-muted">
        Remember it now? <Link to="/login" className="font-bold text-sarthi-primary underline">Sign in</Link>
      </div>
    </div>
  );
}

function PwField({ label, show, value, onChange, testid }) {
  const [visible, setVisible] = useState(false);
  const effective = show || visible;
  return (
    <div>
      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-sarthi-muted">{label}</label>
      <div className="relative">
        <input
          data-testid={testid}
          type={effective ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-xl border-2 border-sarthi-border bg-white px-4 py-3 pr-12 text-base font-semibold focus:border-sarthi-primary focus:outline-none"
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-sarthi-muted hover:text-sarthi-primary"
          aria-label={effective ? "Hide password" : "Show password"}
        >
          {effective ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      </div>
    </div>
  );
}
